/**
 * Seed packets: one photo of a bed's packets, read, then filed as a bed of
 * plants once the gardener has looked it over (bloom-backend routes/packets.js).
 *
 * Endpoints (every answer also carries `success`):
 * - POST /packets/confirm { file, locationId | newBedName, stageId, items,
 *   trayIds?, upToRoom? } → PacketResult, dated the garden's own day (the
 *   server's; architecture audit A31: no day is sent). On Free past its
 *   plants, 402 FREE_PLANT_LIMIT unless upToRoom. A new variety is named by
 *   the server (audit A35): each item sends the name as typed or read and the
 *   packet's kind (kindToJoin), which the server joins to it under a broad
 *   flower parent ("Odd One Zinnia"), so the review sheet and the Add photos
 *   card file a packet under the same name.
 *
 * - POST /packets/keep { file, trayIds?, items } → KeepResult (B137): the Seed vault's door. Each item is a packet
 *   the gardener ticked; the server keeps it as a seed in her Seed vault (made on demand), no plant, no bed. A
 *   packet whose seed is already in her vault comes marked `inVault` on the scan (the server's rule).
 *
 * Who uses it: features/packets (PacketReview,
 * usePacketsFiled, startingChoices) and features/photo-tray (a tray photo
 * of packets), and api/tray (the types). Nothing is cached; filing
 * invalidates 'plants', 'locations', 'plant-outlook', the varieties list
 * and ['billing'] (Free's room). Auth: the signed-in token, in the garden
 * being tended.
 *
 * @module packets
 */

import { apiClient } from './apiClient';
import { trackEvent } from '@/shared/lib/analytics';
import type { PlantSuggestion } from './plantNames';

/** One packet as read: what is printed on it, and what it was matched to. */
export interface ScannedPacket {
  /** The plant as printed: "Tomato", "Zinnia". */
  kind: string;
  /** The variety as printed, or null when the packet names none. */
  variety: string | null;
  /** The catalogue plant it was read as, or null when Grow has none it fits. */
  plantId: number | null;
  /**
   * The packet said only the crop ("Garlic"), so its Type is "Not sure" (B52): plantId is then the crop's usual type, the
   * server's placement, and the review row starts on Type "Not sure" as the server will file it. Absent or false otherwise.
   */
  typeUnsure?: boolean;
  /** With typeUnsure: the crop (plant group) the packet is filed under, which the confirm sends instead of a type. */
  cropId?: number | null;
  /** That plant's name, or null with it. */
  plantName: string | null;
  /** A variety Grow or the gardener already has, by that name under that plant. */
  varietyId: number | null;
  /** That variety's name, or null with it. */
  varietyName: string | null;
  /** A variety nobody has yet: made as the gardener's own if they keep it. */
  newVariety: string | null;
  /**
   * How the plant will be named once the packet is filed ("Italian Aromatic
   * Sage"; services/grow/plantNaming); null for an unplaced packet.
   */
  placedName: string | null;
  /**
   * The broad flower parent it is placed under ('tender', 'hardy',
   * 'perennial'), the server's word for it; null for any other plant.
   */
  broad: 'tender' | 'hardy' | 'perennial' | null;
  /**
   * Set only once Yes to a suggestion placed it (lib/acceptSuggestion): the
   * kind the server joins to its new variety under a broad flower parent
   * ("Bee Balm" after "Jacob Cline"), or null for a name already whole. A
   * packet as read has none, and its printed kind is joined (kindToJoin).
   */
  joinKind?: string | null;
  /** Where the packet is in the photo, [ymin, xmin, ymax, xmax] on 0-1000, or null when not found. */
  box: [number, number, number, number] | null;
  /**
   * For a packet Grow could not file as a plant of its own (plantId null):
   * what Grow thinks it is and the plant to file it under, offered with one
   * Yes ("Grow thinks this is bee balm (Monarda) …"). `as` is how it is filed
   * under a broad flower parent (its variety there); null under any other
   * plant, which is made the gardener's own first. Null or absent for none
   * (and on scans read before 2026-10-10).
   */
  suggestion?: (PlantSuggestion & { as: PacketFiling | null }) | null;
  /** The server says this packet's seed is already in her Seed vault (a vault plant or a kept seed); absent or false otherwise. */
  inVault?: boolean;
}

/** How a packet is filed: its plant and variety (one there is, or a new one by name). */
export interface PacketFiling {
  plantId: number | null;
  plantName: string | null;
  varietyId: number | null;
  varietyName: string | null;
  newVariety: string | null;
}

/** A photo read: the photo, the bed it names, and its packets. Nothing is saved yet. */
export interface PacketScan {
  /** The photo's stored name, which confirming files. */
  file: string;
  /** Signed addresses for the photo at card and thumbnail size. */
  cardUrl: string;
  thumbUrl: string;
  /** The label as written, or null when there was none. */
  label: string | null;
  /** One of their beds (locationId), a new one by the label's name (isNew), or null when nothing named one. */
  bed: { locationId: number | null; name: string; isNew: boolean } | null;
  /**
   * The server says this scan names its bed by the door it came through (a bed's "Add a new plant here"): today's
   * plant card is drawn for it; absent or false, the Seed vault card. The app decides nothing from `bed`.
   */
  bedNamed?: boolean;
  /** Each packet found, in the photo's order. */
  packets: ScannedPacket[];
  /** The Add photos photo it was read from, which leaves the tray once filed; absent for a scan of its own. */
  trayIds?: number[];
  /** True when Grow's photo safety check flagged the photo: no packets were read, and it is being reviewed. */
  underReview?: boolean;
}

/** One packet kept: its plant, and its variety (one there is, a new one of theirs, or neither for "Not sure"). */
export interface PacketItem {
  /** The catalogue plant; left out for a packet whose Type is "Not sure" (cropId and typeUnsure instead). */
  plantId?: number;
  /** With typeUnsure: the crop (plant group) the server files the packet's plant under, on the crop's usual type (B52). */
  cropId?: number;
  /** Type is "Not sure" (B52): the server files the plant on the crop's usual type, flagged. */
  typeUnsure?: boolean;
  /** A variety there is; absent or null for "Not sure" or a new one. */
  varietyId?: number | null;
  /** A new variety of the gardener's own, by name. */
  newVariety?: string | null;
  /**
   * The packet's kind ("Zinnia"), which the server joins to newVariety under a
   * broad flower parent unless the name says it already; null for a name
   * already whole.
   */
  kind?: string | null;
  /** Its packet's place in the photo, to cut it out for the plant; none for the whole photo. */
  box?: [number, number, number, number] | null;
  /** Its own bed, one of theirs (D78 rev 2 §2, the row's change dialog); or newBedName, a bed made for it by name. Neither: the scan's bed. */
  locationId?: number;
  newBedName?: string;
}

/**
 * The kind the server joins to a packet's new variety: the one a suggestion
 * accepted set (null for a name already whole), else the printed kind.
 *
 * @param packet - The packet, as read or as placed by Yes to its suggestion.
 * @returns The kind to send, or null for none.
 */
export function kindToJoin(packet: Pick<ScannedPacket, 'kind' | 'joinKind'>): string | null {
  return packet.joinKind !== undefined ? packet.joinKind : packet.kind || null;
}

/** What filing a scan made. */
export interface PacketResult {
  /** The bed they went into. */
  location_id: number;
  location_name: string;
  /** True when the bed was made for them. */
  new_bed: boolean;
  /** Plants made. */
  added: number;
  /** Packets that joined a plant of that variety already in the bed. */
  joined: number;
  /** The plants made or joined. */
  plant_obj_ids: number[];
  /** On Free, plants not added for want of room (asked with upToRoom). */
  left: number;
  /** Each bed used and how many plants went to it (POST /tray/kinds, and POST /packets/confirm since D78, a bed per packet row); absent when it could not be read. */
  beds?: Array<{ location_id: number; location_name: string; plants: number }>;
}

/**
 * Files a scan: the bed made if new, a plant for each packet kept, the packet photo on each.
 *
 * @param scan - The scan's photo, and the Add photos photo it was read from, if it was.
 * @param bed - One of their beds, or a new one's name.
 * @param stageId - Each new plant's stage, or null for not sure.
 * @param items - The packets kept.
 * @param upToRoom - On Free, add up to Free's room and leave the rest (result.left), rather than refuse them all.
 */
export async function confirmPackets(
  scan: Pick<PacketScan, 'file' | 'trayIds'>,
  bed: { locationId: number } | { newBedName: string },
  stageId: number | null,
  items: PacketItem[],
  upToRoom = false,
): Promise<PacketResult> {
  const response = await apiClient.post<PacketResult & { success: boolean }>('/packets/confirm', {
    file: scan.file, ...bed, stageId, items,
    ...(scan.trayIds?.length ? { trayIds: scan.trayIds } : {}),
    ...(upToRoom ? { upToRoom: true } : {}),
  });
  if (response.data.added > 0) trackEvent('plant_added', { how: 'packet', count: response.data.added });
  return response.data;
}

/** One ticked packet, as POST /packets/keep takes it: the plant item (or none) with its name and what is printed. */
export interface KeepItem extends Omit<PacketItem, 'locationId' | 'newBedName' | 'plantId'> {
  /** The catalogue plant; null for a packet Grow could not place, and absent for Type "Not sure" (cropId and typeUnsure). */
  plantId?: number | null;
  /** The row's name in one line, as the row says it. */
  name: string;
  /** The packet's own words, as the server read them. */
  printed: { kind: string; variety: string | null };
  /** For a packet Grow could not place: its own suggestion, so the vault's "Grow thinks this is" still works. */
  suggestion?: ScannedPacket['suggestion'];
}

/** What keeping packets made: the Seed vault, and each seed kept. */
export interface KeepResult {
  /** Always true on an answer. */
  success: boolean;
  /** The Seed vault the seeds went into (made if she had none). */
  vault: { location_id: number; location_name: string };
  /** Each seed kept, by its held row and name. */
  kept: Array<{ held_id: number; name: string }>;
}

/**
 * Keeps the ticked packets of a scan as seeds in the Seed vault; the photo leaves the tray with them.
 *
 * @param scan - The scan's photo, and the Add photos photos it was read from.
 * @param items - The ticked packets.
 * @returns The vault and the seeds kept.
 */
export async function keepPackets(scan: Pick<PacketScan, 'file' | 'trayIds'>, items: KeepItem[]): Promise<KeepResult> {
  const response = await apiClient.post<KeepResult>('/packets/keep', {
    file: scan.file,
    ...(scan.trayIds?.length ? { trayIds: scan.trayIds } : {}),
    items,
  });
  return response.data;
}
